import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Trash, PencilSimple, PauseCircle, PlayCircle, CheckCircle, Clock, Warning,
  CalendarBlank, CurrencyDollar, Receipt, Bank, List, CalendarCheck, CaretLeft, CaretRight,
  UsersThree, User, ClockClockwise, ChartPieSlice, MagnifyingGlass, X,
} from "@phosphor-icons/react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

const fmt = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DOW_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// ── Date helpers (ISO date strings to avoid TZ drift) ────────────
const dateAddPeriod = (iso, freq, customDays) => {
  const [y, m, d] = iso.split("-").map(Number);
  const base = new Date(y, m - 1, d);
  if (freq === "weekly") base.setDate(base.getDate() + 7);
  else if (freq === "biweekly") base.setDate(base.getDate() + 14);
  else if (freq === "monthly") {
    const day = base.getDate();
    base.setDate(1);
    base.setMonth(base.getMonth() + 1);
    const lastDay = new Date(base.getFullYear(), base.getMonth() + 1, 0).getDate();
    base.setDate(Math.min(day, lastDay));
  } else {
    base.setDate(base.getDate() + Math.max(1, parseInt(customDays || 30, 10)));
  }
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, "0");
  const dd = String(base.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

const monthBounds = (year, monthIdx) => {
  const first = new Date(year, monthIdx, 1);
  const last = new Date(year, monthIdx + 1, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return {
    startIso: `${year}-${pad(monthIdx + 1)}-01`,
    endIso: `${year}-${pad(monthIdx + 1)}-${pad(last.getDate())}`,
    firstDow: (first.getDay() + 6) % 7, // Monday=0
    daysInMonth: last.getDate(),
  };
};

const FREQ_LABEL = {
  weekly: "Semanal",
  biweekly: "Quincenal",
  monthly: "Mensual",
  custom: "Personalizada",
};

const STATUS_LABEL = {
  active: { text: "Activo", color: "#00873D", bg: "#dcfce7" },
  paused: { text: "Pausado", color: "#a16207", bg: "#fef3c7" },
  finalized: { text: "Finalizado", color: "#52525B", bg: "#e4e4e7" },
};

const EMPTY_FORM = {
  name: "",
  amount: "",
  currency: "MXN",
  category_id: "",
  description: "",
  frequency: "monthly",
  custom_interval_days: 30,
  day_of_payment: 1,
  start_date: todayISO(),
  end_date: "",
  status: "active",
};

const CAT_COLOR_OPTS = ["#7E57C2", "#FFC000", "#E0000F", "#1E88E5", "#00873D", "#FF7043", "#26A69A", "#52525B"];

function daysUntil(iso) {
  if (!iso) return null;
  const today = new Date(todayISO() + "T00:00:00");
  const target = new Date(iso + "T00:00:00");
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

function nextPaymentBadge(iso) {
  const d = daysUntil(iso);
  if (d === null) return { text: "—", color: "#52525B", bg: "#f4f4f5" };
  if (d < 0) return { text: `Vencido (${Math.abs(d)}d)`, color: "#fff", bg: "#E0000F" };
  if (d === 0) return { text: "HOY", color: "#fff", bg: "#E0000F" };
  if (d <= 7) return { text: `En ${d}d`, color: "#92400e", bg: "#fef3c7" };
  if (d <= 30) return { text: `En ${d}d`, color: "#1e40af", bg: "#dbeafe" };
  return { text: `En ${d}d`, color: "#374151", bg: "#f4f4f5" };
}

export default function RecurringExpenses() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [banks, setBanks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterUrgency, setFilterUrgency] = useState("all"); // all / overdue / week / month
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [toDelete, setToDelete] = useState(null);
  const [confirmingPay, setConfirmingPay] = useState(null);
  const [payForm, setPayForm] = useState({ bank_id: "", payment_date: todayISO(), note: "" });
  const [saving, setSaving] = useState(false);
  // Inline category creation
  const [showCatForm, setShowCatForm] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", color: CAT_COLOR_OPTS[0] });
  // View toggle (list / calendar) + month being viewed for calendar
  const [viewMode, setViewMode] = useState("list");
  const now = new Date();
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth()); // 0-indexed
  // Salary module
  const [employees, setEmployees] = useState([]);
  const [showSalaryForm, setShowSalaryForm] = useState(false);
  const [salaryForm, setSalaryForm] = useState({ employee_id: "", weekly_salary: "", start_date: "" });
  const [salaryPreview, setSalaryPreview] = useState(null); // loaded when confirmingPay is a salary
  const [commissionPreview, setCommissionPreview] = useState(null); // loaded when confirmingPay is a commission
  const [commissionBase, setCommissionBase] = useState("paid"); // "paid" | "billed" | "profit"

  const load = async () => {
    const params = filterStatus !== "all" ? { status: filterStatus } : {};
    const [r, s, b, c, emp] = await Promise.all([
      api.get("/recurring-expenses", { params }),
      api.get("/recurring-expenses/stats"),
      api.get("/banks").catch(() => ({ data: [] })),
      api.get("/expense-categories").catch(() => ({ data: [] })),
      api.get("/hr/employees", { params: { status: "active" } }).catch(() => ({ data: [] })),
    ]);
    setItems(r.data || []);
    setStats(s.data);
    setBanks(b.data || []);
    setCategories(c.data || []);
    setEmployees(emp.data || []);
  };

  useEffect(() => { load(); }, [filterStatus]);

  const filtered = useMemo(() => {
    let arr = items;
    const today = todayISO();
    const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
    const in7Iso = plusDays(7);
    const in30Iso = plusDays(30);
    if (filterUrgency === "overdue") arr = arr.filter((x) => x.status === "active" && (x.next_payment_date || "") < today);
    else if (filterUrgency === "week") arr = arr.filter((x) => x.status === "active" && (x.next_payment_date || "") >= today && (x.next_payment_date || "") <= in7Iso);
    else if (filterUrgency === "month") arr = arr.filter((x) => x.status === "active" && (x.next_payment_date || "") >= today && (x.next_payment_date || "") <= in30Iso);
    if (filterCategoryId !== "all") {
      arr = arr.filter((x) => (filterCategoryId === "__none__" ? !x.category_id : x.category_id === filterCategoryId));
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const tokens = norm(q).split(/\s+/).filter(Boolean);
      arr = arr.filter((x) => {
        const hay = `${norm(x.name)} ${norm(x.description)} ${norm(x.category_name)}`;
        return tokens.every((t) => hay.includes(t));
      });
    }
    return arr;
  }, [items, filterUrgency, filterCategoryId, searchQuery]);

  // Project payment occurrences inside the viewed month for each active recurring expense
  const calendarOccurrences = useMemo(() => {
    const { startIso, endIso } = monthBounds(calYear, calMonth);
    const result = {}; // { "YYYY-MM-DD": [recurringExpense, ...] }
    for (const it of items) {
      if (it.status !== "active" || !it.next_payment_date) continue;
      let cursor = it.next_payment_date;
      let safety = 200;
      // Fast-forward if next_payment_date is before this month
      while (cursor < startIso && safety > 0) {
        cursor = dateAddPeriod(cursor, it.frequency, it.custom_interval_days);
        safety -= 1;
      }
      // If original next_payment_date falls inside the month (overdue or due), include it
      const original = it.next_payment_date;
      if (original >= startIso && original <= endIso) {
        (result[original] = result[original] || []).push(it);
      }
      // Then walk forward inside the month
      while (cursor <= endIso && safety > 0) {
        if (cursor >= startIso && cursor !== original) {
          (result[cursor] = result[cursor] || []).push(it);
        }
        cursor = dateAddPeriod(cursor, it.frequency, it.custom_interval_days);
        safety -= 1;
      }
    }
    return result;
  }, [items, calYear, calMonth]);

  const monthTotalMxn = useMemo(() => {
    let total = 0;
    for (const day of Object.values(calendarOccurrences)) {
      for (const it of day) {
        total += it.currency === "USD" ? (it.amount || 0) * 17 : (it.amount || 0);
      }
    }
    return total;
  }, [calendarOccurrences]);

  // Breakdown por categoría del mes en curso (en MXN) + paleta de fallback si colores se repiten
  const CHART_PALETTE = ["#002FA7", "#E0000F", "#00873D", "#FFC000", "#7E57C2", "#26A69A", "#FF7043", "#1E88E5", "#9E9E9E", "#EC407A"];
  const categoryBreakdown = useMemo(() => {
    const acc = {}; // { catId: { name, color, total, count } }
    for (const day of Object.values(calendarOccurrences)) {
      for (const it of day) {
        const key = it.category_id || "__none__";
        const mxn = it.currency === "USD" ? (it.amount || 0) * 17 : (it.amount || 0);
        if (!acc[key]) {
          acc[key] = {
            name: it.category_name || "Sin categoría",
            color: it.category_color || "#52525B",
            total: 0,
            count: 0,
          };
        }
        acc[key].total += mxn;
        acc[key].count += 1;
      }
    }
    const sorted = Object.values(acc).sort((a, b) => b.total - a.total);
    // Si hay colores repetidos, usa paleta de fallback distinta por categoría
    const distinctColors = new Set(sorted.map((c) => c.color));
    if (distinctColors.size < sorted.length) {
      sorted.forEach((c, i) => { c.color = CHART_PALETTE[i % CHART_PALETTE.length]; });
    }
    return sorted;
  }, [calendarOccurrences]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      amount: it.amount,
      currency: it.currency || "MXN",
      category_id: it.category_id || "",
      description: it.description || "",
      frequency: it.frequency || "monthly",
      custom_interval_days: it.custom_interval_days || 30,
      day_of_payment: it.day_of_payment || 1,
      start_date: it.start_date || todayISO(),
      end_date: it.end_date || "",
      status: it.status || "active",
    });
    setShowForm(true);
  };

  const submitCategory = async (e) => {
    e.preventDefault();
    const name = catForm.name.trim();
    if (!name) return toast.error("Nombre requerido");
    try {
      const { data } = await api.post("/expense-categories", { name, color: catForm.color });
      toast.success("Categoría creada");
      // Refresh categories and auto-select the new one
      const c = await api.get("/expense-categories");
      setCategories(c.data || []);
      setForm((f) => ({ ...f, category_id: data.id }));
      setShowCatForm(false);
      setCatForm({ name: "", color: CAT_COLOR_OPTS[0] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!form.name.trim()) return toast.error("Nombre requerido");
    if (!amt || amt <= 0) return toast.error("Monto inválido");
    if (!form.category_id) return toast.error("Selecciona una categoría (es obligatoria)");
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: amt,
        custom_interval_days: parseInt(form.custom_interval_days, 10) || 30,
        day_of_payment: parseInt(form.day_of_payment, 10) || 1,
        end_date: form.end_date || null,
        category_id: form.category_id || null,
      };
      if (editing) await api.put(`/recurring-expenses/${editing.id}`, payload);
      else await api.post("/recurring-expenses", payload);
      toast.success(editing ? "Gasto recurrente actualizado" : "Gasto recurrente creado");
      setShowForm(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/recurring-expenses/${toDelete.id}`);
      toast.success("Eliminado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setToDelete(null); }
  };

  const togglePause = async (it) => {
    try {
      await api.post(`/recurring-expenses/${it.id}/${it.status === "active" ? "pause" : "resume"}`);
      toast.success(it.status === "active" ? "Pausado" : "Reanudado");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  // When the user toggles the commission base (paid/billed/profit), update
  // the amount override to reflect the selected calculation.
  useEffect(() => {
    if (!commissionPreview || !confirmingPay || confirmingPay.kind !== "commission") return;
    const map = { paid: "on_paid", billed: "on_billed", profit: "on_profit" };
    const key = map[commissionBase] || "on_paid";
    const val = commissionPreview.commission?.[key] ?? 0;
    setPayForm((prev) => ({ ...prev, amount_override: val }));
  }, [commissionBase, commissionPreview, confirmingPay]);

  const openConfirmPay = (it) => {
    setConfirmingPay(it);
    setPayForm({ bank_id: "", payment_date: it.next_payment_date || todayISO(), note: "", amount_override: it.amount });
    setSalaryPreview(null);
    setCommissionPreview(null);
    setCommissionBase("paid");
    if (it.kind === "salary") {
      api.get(`/recurring-expenses/${it.id}/salary-preview`)
        .then((r) => setSalaryPreview(r.data))
        .catch(() => setSalaryPreview(null));
    } else if (it.kind === "commission") {
      api.get(`/recurring-expenses/${it.id}/commission-preview`)
        .then((r) => {
          setCommissionPreview(r.data);
          // Auto-set the amount override to the "paid" base commission (conservador por default)
          const suggested = r.data?.commission?.on_paid ?? 0;
          setPayForm((prev) => ({ ...prev, amount_override: suggested }));
        })
        .catch(() => setCommissionPreview(null));
    }
  };

  const submitPay = async (e) => {
    e.preventDefault();
    if (!payForm.bank_id) return toast.error("Selecciona el banco");
    setSaving(true);
    try {
      const payload = {
        bank_id: payForm.bank_id,
        payment_date: payForm.payment_date,
        note: payForm.note,
      };
      // Only send amount_override if it differs from the recurring expense amount
      const overrideAmt = parseFloat(payForm.amount_override);
      if (!Number.isNaN(overrideAmt) && Math.abs(overrideAmt - confirmingPay.amount) > 0.009) {
        payload.amount_override = overrideAmt;
      }
      await api.post(`/recurring-expenses/${confirmingPay.id}/confirm-payment`, payload);
      toast.success("Pago confirmado · gasto registrado y banco descontado");
      setConfirmingPay(null);
      setSalaryPreview(null);
      setCommissionPreview(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const submitSalary = async (e) => {
    e.preventDefault();
    if (!salaryForm.employee_id) return toast.error("Selecciona un empleado");
    const emp = employees.find((x) => x.id === salaryForm.employee_id);
    const salary = parseFloat(salaryForm.weekly_salary) || (emp?.weekly_salary || 0);
    if (!salary || salary <= 0) return toast.error("Asigna un salario base semanal mayor a 0");
    setSaving(true);
    try {
      const payload = { employee_id: salaryForm.employee_id };
      if (salaryForm.weekly_salary && parseFloat(salaryForm.weekly_salary) !== (emp?.weekly_salary || 0)) {
        payload.weekly_salary = parseFloat(salaryForm.weekly_salary);
      }
      if (salaryForm.start_date) payload.start_date = salaryForm.start_date;
      await api.post("/recurring-expenses/salary", payload);
      toast.success("Salario recurrente creado · todos los sábados");
      setShowSalaryForm(false);
      setSalaryForm({ employee_id: "", weekly_salary: "", start_date: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const dueToday = (stats?.overdue_count || 0) + (items.filter((x) => x.status === "active" && (x.next_payment_date || "") === todayISO()).length);

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="recurring-expenses-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">CONTABILIDAD · AUTOMATIZACIÓN</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Gastos recurrentes</h1>
          <p className="text-zinc-500 mt-1 text-sm">
            Renta, servicios, sueldos y compromisos fijos. Confirma el pago en un click → se crea el gasto real y se descuenta del banco.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowSalaryForm(true)} variant="outline" className="border-[#1E88E5] text-[#1E88E5] hover:bg-[#1E88E5] hover:text-white gap-2" data-testid="new-salary-btn">
            <UsersThree size={16} weight="bold"/> Registrar salario
          </Button>
          <Button onClick={openNew} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-2" data-testid="new-recurring-btn">
            <Plus size={16} weight="bold"/> Nuevo gasto recurrente
          </Button>
        </div>
      </header>

      {/* Alertas in-app cuando hay pagos hoy/vencidos */}
      {dueToday > 0 && (
        <div className="border-l-4 border-[#E0000F] bg-red-50 p-4 flex items-start gap-3" data-testid="recurring-alert-banner">
          <Warning size={22} weight="fill" className="text-[#E0000F] mt-0.5 shrink-0"/>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#7f1d1d]">
              {dueToday} {dueToday === 1 ? "pago" : "pagos"} {dueToday === 1 ? "requiere" : "requieren"} tu atención hoy
            </div>
            <div className="text-sm text-zinc-700">
              Hay gastos recurrentes vencidos o que vencen hoy. Confírmalos para mantener tus libros al día.
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => setFilterUrgency("overdue")} className="border-[#E0000F] text-[#E0000F] hover:bg-[#E0000F] hover:text-white">
            Ver vencidos
          </Button>
        </div>
      )}

      {/* Stats: contadores pequeños + total del mes con navegación */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="border border-zinc-200 bg-white p-4">
              <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Activos</div>
              <div className="font-heading text-3xl font-bold text-[#002FA7] mt-1 numeric" data-testid="stat-active">{stats.active_count}</div>
            </div>
            <button onClick={() => setFilterUrgency("overdue")} className="border border-zinc-200 bg-white p-4 text-left hover:border-[#E0000F]">
              <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Vencidos</div>
              <div className="font-heading text-3xl font-bold text-[#E0000F] mt-1 numeric" data-testid="stat-overdue">{stats.overdue_count}</div>
            </button>
          </div>
          <div className="border-2 border-[#002FA7] bg-white p-4 flex items-center justify-between gap-4" data-testid="month-total-card">
            <div className="flex items-center gap-2">
              <button onClick={() => {
                const m = calMonth - 1;
                if (m < 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(m);
              }} className="p-1.5 border border-zinc-200 hover:bg-zinc-50" data-testid="month-total-prev" title="Mes anterior"><CaretLeft size={14}/></button>
              <button onClick={() => {
                const m = calMonth + 1;
                if (m > 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(m);
              }} className="p-1.5 border border-zinc-200 hover:bg-zinc-50" data-testid="month-total-next" title="Mes siguiente"><CaretRight size={14}/></button>
              <div className="ml-2">
                <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Total del mes</div>
                <div className="font-heading text-sm font-bold text-zinc-800" data-testid="month-total-label">{MONTHS_ES[calMonth]} {calYear}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-heading text-4xl font-extrabold text-[#002FA7] numeric leading-none" data-testid="month-total-amount">
                MX${Number(monthTotalMxn).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </div>
              <button onClick={() => { const n = new Date(); setCalYear(n.getFullYear()); setCalMonth(n.getMonth()); }} className="mt-1 text-[10px] text-zinc-500 hover:text-[#002FA7] underline" data-testid="month-total-today">
                Volver al mes en curso
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search + category filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold"/>
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nombre, descripción o categoría..."
            className="pl-9 pr-8"
            data-testid="recurring-search"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600"
              data-testid="recurring-search-clear"
            >
              <X size={14} weight="bold"/>
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Categoría:</span>
          <Select value={filterCategoryId} onValueChange={setFilterCategoryId}>
            <SelectTrigger className="w-[200px] h-9" data-testid="recurring-cat-filter">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="__none__"><span className="text-zinc-400">Sin categoría</span></SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: c.color }}/>
                    {c.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(searchQuery || filterCategoryId !== "all" || filterUrgency !== "all" || filterStatus !== "all") && (
          <span className="text-xs text-zinc-500 font-mono-data" data-testid="filter-results-count">
            {filtered.length} resultado{filtered.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-mono-data uppercase tracking-wider text-zinc-500">Urgencia:</span>
        {[
          { v: "all", label: "Todos" },
          { v: "overdue", label: "Vencidos" },
          { v: "week", label: "Próximos 7d" },
          { v: "month", label: "Próximos 30d" },
        ].map((o) => (
          <button key={o.v} onClick={() => setFilterUrgency(o.v)}
            className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all ${filterUrgency === o.v ? "bg-[#002FA7] text-white border-transparent" : "bg-white border-zinc-200 text-zinc-600"}`}
            data-testid={`filter-urgency-${o.v}`}>
            {o.label}
          </button>
        ))}
        <span className="ml-4 text-xs font-mono-data uppercase tracking-wider text-zinc-500">Estatus:</span>
        {[
          { v: "all", label: "Todos" },
          { v: "active", label: "Activos" },
          { v: "paused", label: "Pausados" },
          { v: "finalized", label: "Finalizados" },
        ].map((o) => (
          <button key={o.v} onClick={() => setFilterStatus(o.v)}
            className={`px-3 py-1 text-xs font-medium rounded-sm border transition-all ${filterStatus === o.v ? "bg-zinc-800 text-white border-transparent" : "bg-white border-zinc-200 text-zinc-600"}`}
            data-testid={`filter-status-${o.v}`}>
            {o.label}
          </button>
        ))}
      </div>

      {/* View toggle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-sm border border-zinc-200 bg-white overflow-hidden">
          <button onClick={() => setViewMode("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === "list" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
            data-testid="view-list">
            <List size={14} weight="bold"/> Lista
          </button>
          <button onClick={() => setViewMode("calendar")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === "calendar" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
            data-testid="view-calendar">
            <CalendarCheck size={14} weight="bold"/> Calendario
          </button>
          <button onClick={() => setViewMode("chart")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition-colors ${viewMode === "chart" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
            data-testid="view-chart">
            <ChartPieSlice size={14} weight="bold"/> Gráfica
          </button>
        </div>
        {(viewMode === "calendar" || viewMode === "chart") && (
          <div className="inline-flex items-center gap-2">
            <button onClick={() => {
              const m = calMonth - 1;
              if (m < 0) { setCalMonth(11); setCalYear(calYear - 1); } else setCalMonth(m);
            }} className="p-1.5 border border-zinc-200 hover:bg-zinc-50" data-testid="cal-prev"><CaretLeft size={14}/></button>
            <span className="font-heading font-semibold text-zinc-800 min-w-[180px] text-center" data-testid="cal-month-label">{MONTHS_ES[calMonth]} {calYear}</span>
            <button onClick={() => {
              const m = calMonth + 1;
              if (m > 11) { setCalMonth(0); setCalYear(calYear + 1); } else setCalMonth(m);
            }} className="p-1.5 border border-zinc-200 hover:bg-zinc-50" data-testid="cal-next"><CaretRight size={14}/></button>
            <Button size="sm" variant="outline" onClick={() => { const n = new Date(); setCalYear(n.getFullYear()); setCalMonth(n.getMonth()); }} data-testid="cal-today">Hoy</Button>
          </div>
        )}
      </div>

      {/* Chart view: breakdown por categoría */}
      {viewMode === "chart" && (
        <div className="border border-zinc-200 bg-white p-5" data-testid="recurring-chart">
          {categoryBreakdown.length === 0 ? (
            <div className="text-center py-16 text-zinc-400 dotted-bg">Sin gastos proyectados en {MONTHS_ES[calMonth]} {calYear}</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
              {/* Pie chart */}
              <div className="flex flex-col items-center">
                <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 mb-2">Distribución por categoría · MXN</div>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={categoryBreakdown}
                      dataKey="total"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={120}
                      innerRadius={60}
                      paddingAngle={2}
                      label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {categoryBreakdown.map((c, i) => (
                        <Cell key={i} fill={c.color}/>
                      ))}
                    </Pie>
                    <ReTooltip
                      formatter={(v, n) => [`MX$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, n]}
                      contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: 0, fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center mt-2">
                  <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Total</div>
                  <div className="font-heading text-2xl font-extrabold text-[#002FA7] numeric">MX${Number(monthTotalMxn).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                </div>
              </div>

              {/* Bar list */}
              <div className="space-y-2">
                <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 mb-2">Desglose</div>
                <div className="space-y-2.5">
                  {categoryBreakdown.map((c, i) => {
                    const pct = monthTotalMxn > 0 ? (c.total / monthTotalMxn) * 100 : 0;
                    return (
                      <div key={i} className="space-y-1" data-testid={`cat-row-${i}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="inline-flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm" style={{ background: c.color }}/>
                            <span className="font-semibold text-zinc-800">{c.name}</span>
                            <span className="text-[10px] font-mono-data text-zinc-400">· {c.count} pago{c.count === 1 ? "" : "s"}</span>
                          </span>
                          <span className="font-semibold numeric text-zinc-800">
                            MX${Number(c.total).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            <span className="ml-2 text-[10px] font-mono-data text-zinc-400">{pct.toFixed(1)}%</span>
                          </span>
                        </div>
                        <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden">
                          <div className="h-full transition-all" style={{ width: `${pct}%`, background: c.color }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Calendar view */}
      {viewMode === "calendar" && (
        <div className="border border-zinc-200 bg-white" data-testid="recurring-calendar">
          <div className="grid grid-cols-7 bg-zinc-50 border-b border-zinc-200">
            {DOW_ES.map((d) => (
              <div key={d} className="text-[10px] uppercase tracking-widest font-mono-data text-zinc-500 px-2 py-2 text-center">{d}</div>
            ))}
          </div>
          {(() => {
            const { firstDow, daysInMonth } = monthBounds(calYear, calMonth);
            const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7;
            const cells = [];
            const todayStr = todayISO();
            for (let i = 0; i < totalCells; i += 1) {
              const dayNum = i - firstDow + 1;
              if (dayNum < 1 || dayNum > daysInMonth) {
                cells.push(<div key={i} className="min-h-[110px] bg-zinc-50/40 border-r border-b border-zinc-100"/>);
                continue;
              }
              const pad = (n) => String(n).padStart(2, "0");
              const iso = `${calYear}-${pad(calMonth + 1)}-${pad(dayNum)}`;
              const occs = calendarOccurrences[iso] || [];
              const isToday = iso === todayStr;
              const isOverdueDay = iso < todayStr;
              cells.push(
                <div key={i} className={`min-h-[110px] border-r border-b border-zinc-100 p-1.5 flex flex-col gap-0.5 ${isToday ? "bg-blue-50/60" : "bg-white"}`} data-testid={`cal-day-${iso}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-bold ${isToday ? "bg-[#002FA7] text-white rounded-full w-5 h-5 inline-flex items-center justify-center" : isOverdueDay ? "text-zinc-400" : "text-zinc-700"}`}>{dayNum}</span>
                    {occs.length > 0 && (
                      <span className="text-[9px] font-semibold text-zinc-400">{occs.length}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    {occs.slice(0, 3).map((o, idx) => (
                      <button
                        key={`${o.id}-${idx}`}
                        onClick={() => openConfirmPay({ ...o, next_payment_date: iso })}
                        className={`text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate font-medium hover:opacity-80 transition-opacity ${isOverdueDay && !isToday ? "ring-1 ring-[#E0000F]" : ""}`}
                        style={{ background: (o.category_color || "#7E57C2") + "20", color: o.category_color || "#7E57C2" }}
                        title={`${o.name} · ${fmt(o.amount, o.currency)}${isOverdueDay && !isToday ? " · VENCIDO" : ""}`}
                        data-testid={`cal-occ-${o.id}-${iso}`}
                      >
                        <span className="font-semibold">{o.name}</span>
                        <span className="ml-1 numeric opacity-80">{o.currency === "USD" ? "$" : ""}{Number(o.amount).toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
                      </button>
                    ))}
                    {occs.length > 3 && (
                      <span className="text-[10px] text-zinc-400 px-1">+{occs.length - 3} más</span>
                    )}
                  </div>
                </div>,
              );
            }
            return <div className="grid grid-cols-7">{cells}</div>;
          })()}
          <div className="px-3 py-2 border-t border-zinc-200 bg-zinc-50 flex items-center gap-4 flex-wrap text-[11px] text-zinc-600">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-50 border border-blue-200"/> Hoy</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm ring-1 ring-[#E0000F]"/> Vencido</span>
            <span className="ml-auto text-zinc-500">Click en un pago para confirmarlo</span>
          </div>
        </div>
      )}

      {/* List */}
      {viewMode === "list" && (
      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="recurring-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Categoría</th>
              <th className="text-left px-4 py-3">Frecuencia</th>
              <th className="text-right px-4 py-3">Monto</th>
              <th className="text-left px-4 py-3">Próximo pago</th>
              <th className="text-left px-4 py-3">Último pago</th>
              <th className="text-left px-4 py-3">Estatus</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin gastos recurrentes — Crea el primero para automatizar tus pagos fijos</td></tr>
            )}
            {filtered.map((it) => {
              const badge = nextPaymentBadge(it.next_payment_date);
              const stBadge = STATUS_LABEL[it.status] || STATUS_LABEL.active;
              return (
                <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`row-${it.id}`}>
                  <td className="px-4 py-3 font-semibold text-zinc-800">
                    <div className="flex items-center gap-1.5">
                      {it.name}
                      {it.kind === "salary" && <span className="px-1.5 py-0.5 rounded bg-[#1E88E5] text-white text-[9px] tracking-wider font-mono-data">SALARIO</span>}
                      {it.kind === "commission" && <span className="px-1.5 py-0.5 rounded bg-[#E91E63] text-white text-[9px] tracking-wider font-mono-data" title={`${Number(it.commission_pct ?? 10)}% sobre ventas cobradas`}>COMISIÓN {Number(it.commission_pct ?? 10)}%</span>}
                    </div>
                    {it.description && <div className="text-xs text-zinc-500 font-normal mt-0.5 max-w-[280px] truncate">{it.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">
                    {it.category_name ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-sm" style={{ background: (it.category_color || "#7E57C2") + "20", color: it.category_color || "#7E57C2" }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: it.category_color || "#7E57C2" }}/>
                        {it.category_name}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-700">
                    {FREQ_LABEL[it.frequency] || it.frequency}
                    {it.frequency === "custom" && <span className="text-zinc-400 ml-1">({it.custom_interval_days}d)</span>}
                  </td>
                  <td className="px-4 py-3 text-right numeric font-semibold text-[#E0000F]">−{fmt(it.amount, it.currency)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-data text-xs text-zinc-700">{it.next_payment_date || "—"}</span>
                      {it.status === "active" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide" style={{ background: badge.bg, color: badge.color }}>{badge.text}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono-data text-xs text-zinc-500">{it.last_paid_date || "—"}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide" style={{ background: stBadge.bg, color: stBadge.color }}>{stBadge.text}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {it.status === "active" && (
                      <button onClick={() => openConfirmPay(it)} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 bg-[#00873D] hover:bg-[#006b30] text-white rounded mr-1" data-testid={`confirm-pay-${it.id}`} title="Confirmar pago">
                        <CheckCircle size={12} weight="fill"/> Pagar
                      </button>
                    )}
                    <button onClick={() => togglePause(it)} className="p-2 hover:bg-amber-100 text-amber-700 rounded" data-testid={`toggle-pause-${it.id}`} title={it.status === "active" ? "Pausar" : "Reanudar"}>
                      {it.status === "active" ? <PauseCircle size={14}/> : <PlayCircle size={14}/>}
                    </button>
                    <button onClick={() => openEdit(it)} className="p-2 hover:bg-blue-100 text-[#002FA7] rounded ml-1" data-testid={`edit-${it.id}`} title="Editar">
                      <PencilSimple size={14}/>
                    </button>
                    <button onClick={() => setToDelete(it)} className="p-2 hover:bg-red-100 text-[#E0000F] rounded ml-1" data-testid={`delete-${it.id}`} title="Eliminar">
                      <Trash size={14}/>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* Salary creation dialog */}
      <Dialog open={showSalaryForm} onOpenChange={(o) => { if (!o) setShowSalaryForm(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <UsersThree size={22} weight="fill" className="text-[#1E88E5]"/> Registrar salario
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Crea un pago recurrente semanal (cada sábado) para un empleado. Sin fecha de fin: se finaliza solo al dar de baja al empleado.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitSalary} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Empleado *</Label>
              <Select value={salaryForm.employee_id || undefined} onValueChange={(v) => {
                const emp = employees.find((x) => x.id === v);
                setSalaryForm({ ...salaryForm, employee_id: v, weekly_salary: emp?.weekly_salary || "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="salary-emp">
                  <SelectValue placeholder="Selecciona empleado"/>
                </SelectTrigger>
                <SelectContent>
                  {employees.length === 0 && <div className="px-3 py-2 text-xs text-zinc-400">No hay empleados activos. Crea uno en /hr/empleados primero.</div>}
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      <span className="inline-flex items-center gap-2">
                        <User size={14} weight="duotone"/>
                        {emp.name} {emp.weekly_salary > 0 && <span className="text-zinc-400 text-xs numeric">· MX${Number(emp.weekly_salary).toLocaleString("en-US")}</span>}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Salario base semanal (MXN) *</Label>
              <Input type="number" min="0" step="100" value={salaryForm.weekly_salary} onChange={(e) => setSalaryForm({ ...salaryForm, weekly_salary: e.target.value })} placeholder="ej. 3000" required className="mt-1 numeric" data-testid="salary-amount"/>
              <p className="text-[10px] text-zinc-400 mt-1">Se carga del perfil del empleado. Edítalo aquí si necesitas cambiarlo solo para este pago recurrente.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Primer pago (sábado)</Label>
              <Input type="date" value={salaryForm.start_date} onChange={(e) => setSalaryForm({ ...salaryForm, start_date: e.target.value })} className="mt-1" data-testid="salary-start"/>
              <p className="text-[10px] text-zinc-400 mt-1">Déjalo vacío → próximo sábado.</p>
            </div>
            <div className="bg-[#1E88E5]/10 border border-[#1E88E5]/30 p-3 rounded text-xs text-[#0f4c81]">
              Al pagar cada semana podrás ver el resumen del reloj checador (presentes, faltas, retardos) y ajustar el monto antes de confirmar.
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowSalaryForm(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#1E88E5] hover:bg-[#1565c0] text-white" data-testid="salary-submit">
                {saving ? "Creando..." : "Crear salario recurrente"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Form modal */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{editing ? "Editar gasto recurrente" : "Nuevo gasto recurrente"}</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Programa un compromiso fijo (renta, sueldo, servicio) con su frecuencia y fecha de inicio.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Renta oficina, Netflix, Sueldo Juan..." className="mt-1" data-testid="form-name"/>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Monto *</Label>
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="mt-1 numeric" data-testid="form-amount"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Moneda</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="mt-1" data-testid="form-currency"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider">Categoría *</Label>
                <button type="button" onClick={() => setShowCatForm(true)} className="text-[11px] font-semibold text-[#002FA7] hover:underline" data-testid="open-new-cat">
                  + Nueva categoría
                </button>
              </div>
              <Select value={form.category_id || undefined} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                <SelectTrigger className={`mt-1 ${!form.category_id ? "border-amber-400" : ""}`} data-testid="form-category">
                  <SelectValue placeholder="Selecciona categoría"/>
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: c.color }}/>
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-zinc-400 mt-1">Las categorías son las mismas que en Gastos. Crea nuevas si la que necesitas no aparece.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Frecuencia *</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                  <SelectTrigger className="mt-1" data-testid="form-frequency"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="biweekly">Quincenal</SelectItem>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="custom">Personalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.frequency === "custom" ? (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Días entre pagos *</Label>
                  <Input type="number" min="1" value={form.custom_interval_days} onChange={(e) => setForm({ ...form, custom_interval_days: e.target.value })} className="mt-1" data-testid="form-custom-days"/>
                </div>
              ) : form.frequency === "monthly" ? (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Día del mes</Label>
                  <Input type="number" min="1" max="31" value={form.day_of_payment} onChange={(e) => setForm({ ...form, day_of_payment: e.target.value })} className="mt-1" data-testid="form-day"/>
                </div>
              ) : <div/>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Fecha inicio *</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required className="mt-1" data-testid="form-start"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Fecha fin (opcional)</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="form-end"/>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Detalles, número de cuenta, contacto..." className="mt-1" data-testid="form-description"/>
            </div>
            {editing && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Estatus</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="form-status"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="paused">Pausado</SelectItem>
                    <SelectItem value="finalized">Finalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditing(null); }}>Cancelar</Button>
              <Button type="submit" disabled={saving || !form.category_id} className="bg-[#002FA7] hover:bg-[#002277] text-white disabled:opacity-50" data-testid="form-submit">
                {saving ? "Guardando..." : (editing ? "Guardar cambios" : "Crear")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Confirm payment modal */}
      <Dialog open={!!confirmingPay} onOpenChange={(o) => { if (!o) setConfirmingPay(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <CheckCircle size={22} weight="fill" className="text-[#00873D]"/> Confirmar pago
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Crea el gasto real, descuenta el monto del banco y avanza la próxima fecha de pago automáticamente.</DialogDescription>
          </DialogHeader>
          {confirmingPay && (
            <form onSubmit={submitPay} className="space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 p-3 rounded">
                <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 flex items-center justify-between">
                  <span>
                    {confirmingPay.kind === "salary" ? "Salario semanal"
                      : confirmingPay.kind === "commission" ? "Comisión semanal"
                      : "Gasto"}
                  </span>
                  {confirmingPay.kind === "salary" && <span className="px-1.5 py-0.5 rounded bg-[#1E88E5] text-white text-[10px] tracking-wider">SALARIO</span>}
                  {confirmingPay.kind === "commission" && <span className="px-1.5 py-0.5 rounded bg-[#E91E63] text-white text-[10px] tracking-wider">COMISIÓN</span>}
                </div>
                <div className="font-semibold text-zinc-800">{confirmingPay.name}</div>
                <div className="text-2xl font-heading font-bold text-[#E0000F] numeric mt-1">−{fmt(confirmingPay.amount, confirmingPay.currency)}</div>
                <div className="text-xs text-zinc-500 mt-1">Vencimiento: <span className="font-mono-data">{confirmingPay.next_payment_date}</span></div>
              </div>

              {/* Commission preview */}
              {confirmingPay.kind === "commission" && (
                <div className="border border-[#E91E63]/30 bg-[#E91E63]/5 p-3 rounded" data-testid="commission-preview-box">
                  <div className="text-xs font-mono-data uppercase tracking-wider text-[#E91E63] flex items-center justify-between mb-2">
                    <span>Desglose · semana de pago</span>
                    <span className="text-zinc-500">{Number(commissionPreview?.commission_pct ?? confirmingPay.commission_pct ?? 10)}% por venta cobrada</span>
                  </div>
                  {!commissionPreview ? (
                    <div className="text-xs text-zinc-400">Cargando desglose...</div>
                  ) : (
                    <div className="space-y-3">
                      <div className="text-[11px] text-zinc-600">
                        Vendedor: <b>{commissionPreview.seller_name}</b>  ·  Semana <b className="font-mono-data">{commissionPreview.week_start}</b> → <b className="font-mono-data">{commissionPreview.week_end}</b>  ·  <b>{commissionPreview.totals.count}</b> {commissionPreview.totals.count === 1 ? "nota" : "notas"}
                      </div>

                      {/* 3-base selector */}
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { key: "paid",   label: "Cobrado",   sub: "cash real",     total: commissionPreview.totals.paid_mxn,   commission: commissionPreview.commission.on_paid,   color: "#00873D" },
                          { key: "billed", label: "Facturado", sub: "con saldo",     total: commissionPreview.totals.billed_mxn, commission: commissionPreview.commission.on_billed, color: "#1E88E5" },
                          { key: "profit", label: "Utilidad",  sub: "margen real",   total: commissionPreview.totals.profit_mxn, commission: commissionPreview.commission.on_profit, color: "#7E57C2" },
                        ].map((opt) => {
                          const active = commissionBase === opt.key;
                          return (
                            <button
                              type="button"
                              key={opt.key}
                              onClick={() => setCommissionBase(opt.key)}
                              className={`text-left border rounded-sm p-2 transition-all ${active ? "shadow-sm" : "border-zinc-200 hover:border-zinc-300 bg-white"}`}
                              style={active ? { borderColor: opt.color, background: opt.color + "10" } : {}}
                              data-testid={`commission-base-${opt.key}`}
                            >
                              <div className="text-[9px] uppercase font-mono-data tracking-wider" style={{ color: opt.color }}>{opt.label}</div>
                              <div className="text-[10px] text-zinc-500 -mt-0.5">{opt.sub}</div>
                              <div className="text-[10px] text-zinc-600 mt-1 numeric">Base: MX${Number(opt.total).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              <div className="text-base font-heading font-bold numeric" style={{ color: opt.color }}>MX${Number(opt.commission).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Sales table */}
                      {commissionPreview.sales.length > 0 ? (
                        <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-sm bg-white">
                          <table className="w-full text-[11px]">
                            <thead className="bg-zinc-50 text-zinc-500 uppercase text-[9px] sticky top-0">
                              <tr>
                                <th className="text-left px-2 py-1">Folio</th>
                                <th className="text-left px-2 py-1">Fecha</th>
                                <th className="text-left px-2 py-1">Cliente</th>
                                <th className="text-right px-2 py-1">Facturado</th>
                                <th className="text-right px-2 py-1">Cobrado</th>
                                <th className="text-right px-2 py-1">Utilidad</th>
                              </tr>
                            </thead>
                            <tbody>
                              {commissionPreview.sales.map((s) => (
                                <tr key={s.id} className="border-t border-zinc-100" data-testid={`commission-sale-${s.id}`}>
                                  <td className="px-2 py-1 font-mono-data text-[#002FA7]">#{String(s.folio).padStart(6,"0")}</td>
                                  <td className="px-2 py-1 text-zinc-600">{s.invoice_date}</td>
                                  <td className="px-2 py-1 text-zinc-800 truncate max-w-[140px]">{s.client_name}</td>
                                  <td className="px-2 py-1 text-right numeric text-[#1E88E5]">MX${Number(s.total_mxn).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className="px-2 py-1 text-right numeric text-[#00873D]">MX${Number(s.paid_mxn).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td className={`px-2 py-1 text-right numeric ${s.profit_mxn < 0 ? "text-[#E0000F]" : "text-[#7E57C2]"}`}>MX${Number(s.profit_mxn).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-[11px] text-zinc-400 text-center py-3 dotted-bg">Sin ventas en este periodo</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Salary attendance preview */}
              {confirmingPay.kind === "salary" && (
                <div className="border border-[#1E88E5]/30 bg-[#1E88E5]/5 p-3 rounded" data-testid="salary-preview-box">
                  <div className="text-xs font-mono-data uppercase tracking-wider text-[#1E88E5] flex items-center gap-1.5 mb-2">
                    <ClockClockwise size={14} weight="bold"/> Reloj checador · semana
                  </div>
                  {!salaryPreview ? (
                    <div className="text-xs text-zinc-400">Cargando resumen...</div>
                  ) : (
                    <div className="space-y-1.5">
                      <div className="text-[11px] text-zinc-500">
                        Semana <b className="font-mono-data">{salaryPreview.week_start}</b> → <b className="font-mono-data">{salaryPreview.week_end}</b>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="bg-white border border-zinc-200 p-2 rounded">
                          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Presentes</div>
                          <div className="font-heading text-lg font-bold text-emerald-600 numeric">{salaryPreview.attendance.days_present}</div>
                        </div>
                        <div className="bg-white border border-zinc-200 p-2 rounded">
                          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Faltas</div>
                          <div className="font-heading text-lg font-bold text-[#E0000F] numeric">{salaryPreview.attendance.days_absent}</div>
                        </div>
                        <div className="bg-white border border-zinc-200 p-2 rounded">
                          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Retardos</div>
                          <div className="font-heading text-lg font-bold text-amber-600 numeric">{salaryPreview.attendance.days_late}</div>
                          {salaryPreview.attendance.late_minutes_total > 0 && (
                            <div className="text-[9px] text-zinc-500 numeric">{salaryPreview.attendance.late_minutes_total}min</div>
                          )}
                        </div>
                        <div className="bg-white border border-zinc-200 p-2 rounded">
                          <div className="text-[9px] uppercase tracking-wider text-zinc-500">Horas</div>
                          <div className="font-heading text-lg font-bold text-zinc-800 numeric">{salaryPreview.attendance.effective_hours}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Editable amount (always shown — useful for salary overrides) */}
              <div>
                <Label className="text-xs uppercase tracking-wider">Monto a pagar (MXN)</Label>
                <Input type="number" step="0.01" min="0" value={payForm.amount_override} onChange={(e) => setPayForm({ ...payForm, amount_override: e.target.value })} className="mt-1 numeric font-semibold" data-testid="pay-amount"/>
                {confirmingPay.kind === "salary" && (
                  <p className="text-[10px] text-zinc-400 mt-1">Ajusta si hay descuentos, bonos o permisos. Base: <b className="text-zinc-600">MX${Number(confirmingPay.amount).toLocaleString("en-US")}</b></p>
                )}
                {confirmingPay.kind === "commission" && (
                  <p className="text-[10px] text-zinc-400 mt-1">Base seleccionada: <b className="text-zinc-600">{commissionBase === "paid" ? "sobre cobrado (default)" : commissionBase === "billed" ? "sobre facturado" : "sobre utilidad"}</b> · Puedes ajustar el monto libremente.</p>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">Banco que paga *</Label>
                <Select value={payForm.bank_id} onValueChange={(v) => setPayForm({ ...payForm, bank_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="pay-bank">
                    <SelectValue placeholder="Selecciona banco"/>
                  </SelectTrigger>
                  <SelectContent>
                    {banks.filter((b) => b.currency === confirmingPay.currency).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        <span className="inline-flex items-center gap-2">
                          <Bank size={14} weight="duotone"/>
                          {b.name} · {b.currency} {Number(b.balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Fecha de pago</Label>
                <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="mt-1" data-testid="pay-date"/>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Nota (opcional)</Label>
                <Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} placeholder="Referencia, ticket..." className="mt-1"/>
              </div>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded text-xs text-amber-900">
                Al confirmar: se creará un gasto real en <b>Gastos</b>, se descontará del banco seleccionado y se programará la siguiente fecha de pago automáticamente.
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setConfirmingPay(null)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#00873D] hover:bg-[#006b30] text-white" data-testid="pay-submit">
                  {saving ? "Procesando..." : "Confirmar pago"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* New category dialog (inline) */}
      <Dialog open={showCatForm} onOpenChange={setShowCatForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Nueva categoría</DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">Esta categoría aparecerá también en el módulo de Gastos.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCategory} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
              <Input autoFocus value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} required placeholder="Renta, Servicios, Sueldos..." className="mt-1" data-testid="new-cat-name"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Color</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {CAT_COLOR_OPTS.map((c) => (
                  <button type="button" key={c} onClick={() => setCatForm({ ...catForm, color: c })}
                    className={`w-7 h-7 rounded-sm border-2 ${catForm.color === c ? "border-zinc-900" : "border-transparent"}`}
                    style={{ background: c }}/>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCatForm(false)}>Cancelar</Button>
              <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="new-cat-submit">Crear</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar gasto recurrente?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && <>Se eliminará <b>{toDelete.name}</b> del calendario de pagos recurrentes. Los gastos ya confirmados no se afectan.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-[#E0000F] hover:bg-[#b8000c] text-white" data-testid="delete-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
